import { type TranscriptEntry } from '@apps/web/components/phoebe_chat/transcript_types';
import { getV3ToolActivityPresentation } from '@apps/web/features/phoebe_chat_v3/model/activity_presentation';
import {
    adaptInteractiveTool,
    isInteractiveTool,
} from '@apps/web/features/phoebe_chat_v3/model/adapt_interactive_tool';
import {
    collapseDataCards,
    collapseOutreachLinks,
} from '@apps/web/features/phoebe_chat_v3/model/collapse_data_cards';
import { groupV3CommsApprovals } from '@apps/web/features/phoebe_chat_v3/model/group_comms_approvals';
import { getResolvedQuestionActionEntry } from '@apps/web/features/phoebe_chat_v3/model/question_answer';
import {
    appendMilestone,
    bufferReasoning,
    createMutableReasoningEntry,
    displayReasoning,
    finalizedReasoningEntry,
    flushPendingReasoning,
    type MutableReasoningEntry,
} from '@apps/web/features/phoebe_chat_v3/model/reasoning_trace';
import {
    type V3ChatEntry,
    type V3MessageAuthor,
    type V3MessageEntry,
    type V3TranscriptProjection,
} from '@apps/web/features/phoebe_chat_v3/model/transcript';

type SourceEntry<Kind extends TranscriptEntry['kind']> = Extract<
    TranscriptEntry,
    { readonly kind: Kind }
>;
type SourceMessageEntry = SourceEntry<'message'>;
type SourceThoughtEntry = SourceEntry<'thought'>;
type SourceToolEntry = SourceEntry<'tool-execution'>;
type SourceStaticEntry = Exclude<
    TranscriptEntry,
    SourceMessageEntry | SourceThoughtEntry | SourceToolEntry
>;

function valueRevision(value: unknown): string {
    if (value === undefined) {
        return 'undefined';
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function buildRevision(parts: ReadonlyArray<unknown>): string {
    return parts.map(valueRevision).join('\u001f');
}

function messageAuthor(entry: SourceMessageEntry): V3MessageAuthor | null {
    if (entry.role !== 'user') {
        return null;
    }
    if (entry.sentBy) {
        return {
            displayName: entry.sentBy.display_name,
            colorKey: entry.sentBy.user_id,
        };
    }
    if (entry.source === 'slack' && entry.authorLabel) {
        return {
            displayName: entry.authorLabel,
            colorKey: entry.authorLabel,
        };
    }
    return null;
}

function adaptMessage(entry: SourceMessageEntry): V3MessageEntry {
    const author = messageAuthor(entry);
    const uploadAttachments = entry.uploadAttachments;
    return {
        kind: 'message',
        id: entry.id,
        createdAt: entry.createdAt,
        role: entry.role,
        content: entry.content,
        references: entry.references,
        author,
        isError: entry.isRunError === true,
        pendingDeliveryMode: entry.pendingDeliveryMode ?? null,
        sourceItemId: entry.agentItemId ?? null,
        ...(uploadAttachments && uploadAttachments.length > 0
            ? { uploadAttachments }
            : {}),
        revision: buildRevision([
            entry.role,
            entry.content,
            entry.references,
            author,
            entry.isRunError,
            entry.pendingDeliveryMode,
            entry.agentItemId,
            uploadAttachments,
        ]),
    };
}

function adaptStaticEntry(entry: SourceStaticEntry): V3ChatEntry | null {
    switch (entry.kind) {
        case 'activity':
            if (
                entry.tone !== 'destructive' &&
                entry.canRetryRunError !== true &&
                entry.visibleInMainTranscript !== true &&
                entry.sourceEventType !== 'poke' &&
                entry.sourceEventType !== 'watch'
            ) {
                return null;
            }
            return {
                kind: 'status',
                id: entry.id,
                createdAt: entry.createdAt,
                label: entry.label,
                detail: entry.detail,
                tone: entry.tone ?? 'default',
                canRetry: entry.canRetryRunError === true,
                revision: buildRevision([
                    entry.label,
                    entry.detail,
                    entry.tone,
                    entry.canRetryRunError,
                ]),
            };
        case 'recommendation-batch':
            return {
                kind: 'status',
                id: entry.id,
                createdAt: entry.createdAt,
                label: entry.aggregateArrived
                    ? 'Recommendations ready'
                    : 'Reviewing recommendations',
                detail:
                    entry.candidateCount === null
                        ? null
                        : `${entry.candidateCount} candidates`,
                tone: entry.aggregateArrived ? 'success' : 'default',
                canRetry: false,
                revision: buildRevision([
                    entry.aggregateArrived,
                    entry.candidateCount,
                    entry.aggregateDecisions,
                ]),
            };
        case 'subagent-result':
        case 'compaction-marker':
        case 'debug-event':
            return null;
        default: {
            const exhaustiveEntry: never = entry;
            return exhaustiveEntry;
        }
    }
}

export type V3TranscriptAdapter = {
    readonly adapt: (
        entries: ReadonlyArray<TranscriptEntry>,
    ) => V3TranscriptProjection;
};

export function createV3TranscriptAdapter({
    showOutreachPresentations = true,
}: {
    readonly showOutreachPresentations?: boolean;
} = {}): V3TranscriptAdapter {
    return {
        adapt(entries) {
            const projected: Array<V3ChatEntry> = [];
            let openWork: MutableReasoningEntry | null = null;
            let openWorkIndex = -1;
            let activeStatus: V3TranscriptProjection['activeStatus'] = null;

            // One work block until a user/assistant message. Later model
            // passes stay in the same block; questions interrupt as top-level
            // rows without closing it.
            function ensureOpenWork(
                id: string,
                createdAt: string,
            ): MutableReasoningEntry {
                if (openWork) {
                    return openWork;
                }
                openWork = createMutableReasoningEntry({
                    id: `reasoning:${id}`,
                    createdAt,
                });
                openWorkIndex = projected.length;
                return openWork;
            }

            function closeWork(): void {
                if (openWork === null) {
                    return;
                }
                const reasoning = finalizedReasoningEntry(openWork);
                if (reasoning.steps.length > 0) {
                    projected.splice(openWorkIndex, 0, reasoning);
                }
                openWork = null;
                openWorkIndex = -1;
            }

            for (const entry of entries) {
                if (entry.kind === 'message') {
                    closeWork();
                    projected.push(adaptMessage(entry));
                    continue;
                }
                if (entry.kind === 'thought') {
                    const work = ensureOpenWork(entry.id, entry.createdAt);
                    const narration = displayReasoning(entry.content);
                    if (narration !== null) {
                        bufferReasoning(work, {
                            type: 'narration',
                            id: `narration:${entry.id}`,
                            text: narration,
                        });
                    }
                    if (typeof entry.durationMs === 'number') {
                        work.durationsMs.push(entry.durationMs);
                    }
                    continue;
                }
                if (entry.kind === 'tool-execution') {
                    const activity = getV3ToolActivityPresentation(entry);
                    const completion = activity.completion;
                    if (activity.active) {
                        activeStatus = activity.active;
                    }
                    const activityLabel =
                        activity.active?.label ??
                        completion?.milestone.label ??
                        (entry.approvalState === 'pending' ||
                        entry.status === 'pending_approval'
                            ? 'Approval required'
                            : null);
                    const normalizedTool = adaptInteractiveTool({
                        entry,
                        activityLabel,
                        showOutreachPresentations,
                    });
                    if (
                        isInteractiveTool({ entry, normalized: normalizedTool })
                    ) {
                        if (openWork) {
                            flushPendingReasoning(openWork);
                        }
                        projected.push(normalizedTool);
                    }
                    const resolvedQuestionAction =
                        getResolvedQuestionActionEntry(entry);
                    if (resolvedQuestionAction !== null) {
                        closeWork();
                        projected.push(resolvedQuestionAction);
                    }
                    if (
                        completion &&
                        normalizedTool.dataPresentation === null
                    ) {
                        if (completion.display === 'transcript') {
                            closeWork();
                            projected.push({
                                kind: 'status',
                                id: completion.milestone.id,
                                createdAt: entry.createdAt,
                                label: completion.milestone.label,
                                detail: completion.milestone.detail,
                                tone: 'success',
                                canRetry: false,
                                revision: buildRevision([
                                    completion.milestone.label,
                                    completion.milestone.detail,
                                ]),
                            });
                        } else {
                            appendMilestone(
                                ensureOpenWork(
                                    entry.callId ?? entry.id,
                                    entry.createdAt,
                                ),
                                completion.milestone,
                            );
                        }
                    }
                    continue;
                }
                const normalized = adaptStaticEntry(entry);
                if (normalized) {
                    projected.push(normalized);
                }
                if (entry.kind === 'activity' && entry.lifecycleKind) {
                    closeWork();
                    if (entry.lifecycleKind === 'run-started') {
                        activeStatus = null;
                    }
                }
            }

            closeWork();
            return {
                entries: groupV3CommsApprovals(
                    collapseOutreachLinks(collapseDataCards(projected)),
                ),
                activeStatus,
            };
        },
    };
}
